import { withoutBlankCategories } from '../../components/Hackathon/CategoryEditor';

import dayjs from 'dayjs';
import React, { useEffect, useState } from 'react';
import { disableVotingPeriod, updateHackathon } from '../../services/hackathon.service';
import { useNavigate, useParams } from 'react-router-dom';
import { useHackathon } from '../../hooks/useHackathon';
import { Loading } from '../../components/Loading';
import { EditHackathonData, HackathonEditor } from '../../components/Hackathon/HackathonEditor';
import { PageContainer } from '../../components/PageContainer';
import Stack from '@mui/material/Stack/Stack';
import Alert from '@mui/material/Alert/Alert';
import Button from '@mui/material/Button/Button';

export const EditHackathon = () => {

    const { id } = useParams();

    const { hackathon, loading, error, reload } = useHackathon(id);
    const [closingVoting, setClosingVoting] = useState(false);

    const [data, setData] = useState<EditHackathonData>({
        name: hackathon?.name || '',
        description: hackathon?.description || '',
        startDate: hackathon?.startDate ? dayjs(hackathon.startDate) : null,
        endDate: hackathon?.endDate ? dayjs(hackathon.endDate) : null,
        categories: hackathon?.categories || [],
        categoryMeta: hackathon?.categoryMeta || [],
        ideaSubmissionDeadline: hackathon?.ideaSubmissionDeadline ? dayjs(hackathon.ideaSubmissionDeadline) : null,
        inviteCode: hackathon?.inviteCode || '',
        domain: hackathon?.domain || '',
        preferredAuth: hackathon?.preferredAuth || '',
        minTeamSize: hackathon?.minTeamSize || 1,
        maxTeamSize: hackathon?.maxTeamSize || 4,
        blindJudgeScores: hackathon?.blindJudgeScores || false,
        anonymousJudgeScores: hackathon?.anonymousJudgeScores || false,
        participantScores: hackathon?.participantScores || false,
        ideaTemplate: hackathon?.ideaTemplate || '',
        branding: hackathon?.branding || {},
        organization: hackathon?.organization?._id || ''
    })

    const [loaded, setLoaded] = useState(false);

    useEffect(() => {
        if (loaded) return;
        if(!hackathon) return;
        setData({
            name: hackathon?.name || '',
            description: hackathon?.description || '',
            startDate: hackathon?.startDate ? dayjs(hackathon.startDate) : null,
            endDate: hackathon?.endDate ? dayjs(hackathon.endDate) : null,
            categories: hackathon?.categories || [],
            categoryMeta: hackathon?.categoryMeta || [],
            ideaSubmissionDeadline: hackathon?.ideaSubmissionDeadline ? dayjs(hackathon.ideaSubmissionDeadline) : null,
            inviteCode: hackathon?.inviteCode || '',
            domain: hackathon?.domain || '',
            preferredAuth: hackathon?.preferredAuth || '',
            minTeamSize: hackathon?.minTeamSize || 1,
            maxTeamSize: hackathon?.maxTeamSize || 4,
            blindJudgeScores: hackathon?.blindJudgeScores || false,
            anonymousJudgeScores: hackathon?.anonymousJudgeScores || false,
            participantScores: hackathon?.participantScores || false,
            ideaTemplate: hackathon?.ideaTemplate || '',
        branding: hackathon?.branding || {},
        organization: hackathon?.organization?._id || ''
        })
        setLoaded(true);
    }, [hackathon])

    const navigate = useNavigate();

    const [submitError, setSubmitError] = useState('');


    if (!id) {
        return <div>No id provided</div>
    }

     if (error) {
        return <PageContainer>
            <Stack spacing={2}>
                <h1>Error loading the hackathon</h1>
            </Stack>
        </PageContainer>
    }


    if (loading || !loaded) {
        return <Loading />
    }

   
    if (hackathon && hackathon?.votingPeriodOn) {
        // say how to get unstuck rather than just "disabled"
        return (
            // in the form's column, without a card around it (a notice on a white card sat in a white frame)
            <PageContainer variant="plain">
                <Alert severity="warning" sx={{ maxWidth: 960, mx: 'auto' }} action={
                    <Button color="inherit" size="small" disabled={closingVoting} sx={{ whiteSpace: 'nowrap' }} onClick={() => {
                        setClosingVoting(true);
                        disableVotingPeriod(hackathon._id).then(() => reload()).catch(err => console.error(err)).finally(() => setClosingVoting(false));
                    }}>Close voting</Button>
                }>
                    Voting is open, so the hackathon can't be edited: changing categories mid-vote would change what judges are scoring.
                    Close voting to edit, then open it again from the Judging page or the room. Votes already cast are kept.
                </Alert>
            </PageContainer>
        )
    }

    const hasVotes = (hackathon?.results || []).some(r => r.ideas.some(i => (i.judgeVotes || []).some(v => v.score > 0) || i.participantVoteAverage > 0));

    return (
        <>
        <HackathonEditor
            headingLabel={`Editing ${data.name}`}
            // under the heading, in the form's column (on its own page card it sat in a white frame)
            intro={hasVotes && <Alert severity="info">
                Some votes are already in. Renaming a category keeps its scores. A category you add starts unscored: anyone who already voted
                is asked to score it. A category you remove drops out of the totals.
            </Alert>}
            submitLabel='Update Hackathon'
            data={data}
            setData={setData}
            linkedOrganization={hackathon?.organization}
            submitError={submitError}
            plan={hackathon?.plan}
            upgradeUrl={hackathon ? `/h/${hackathon.slug}/upgrade` : undefined}
            onSubmit={() => {
                setSubmitError('');
                updateHackathon(id, {
                    ...withoutBlankCategories(data),
                    startDate: data.startDate?.toDate(),
                    endDate: data.endDate?.toDate(),
                    ideaSubmissionDeadline: data.ideaSubmissionDeadline?.toDate()
                }).then(res => {
                    navigate(`/h/${res.slug}`);
                }).catch(err => {
                    console.error(err);
                    setSubmitError(err.response?.data?.message || 'Could not save the hackathon');
                });
            }} />
        </>
    )
}